import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useMatch, useLocation } from 'react-router-dom';
import { Site, CCOrganization } from '../types';
import type { DomainType } from '../components/SiteCard/CreateSiteDialog';
import { fetchSitesForOrgs, deleteSite, fetchCCOrganizationSummary, SiteDeletionConflictError, SiteDeleteError } from '../services/api';
import { useDeletePolling } from '../hooks/useDeletePolling';
import { getErrorMessage } from '../utils/errorUtils';
import { toast } from '../utils/toast';
import { ROUTES, DEFAULT_SETTINGS_TAB } from '../constants';
import i18n from '../i18n';

/** Command Center view types */
export type CommandCenterView =
  | 'commandCenter'
  | 'defaultOrgConfig'
  | 'models';

/** The view key derived from the URL path segment. */
export type ActiveView =
  | 'chat' | 'edit' | 'addContent' | 'stats' | 'analytics'
  | 'create' | 'manageUsers' | 'tools' | 'history'
  | 'settings' | 'publishing' | 'security' | 'sampleContent' | 'documentation' | 'orgModels'
  | CommandCenterView
  | null;

/**
 * CommandCenterSidebar's nav-row keys. Distinct from CommandCenterView: the
 * landing view 'commandCenter' maps to the 'organizations' row.
 */
export type CommandCenterSubView = 'organizations' | 'defaultOrgConfig' | 'models' | null;

/**
 * Single source of truth for "is this activeView part of Command Center" —
 * used for both UI branching (App.tsx's sidebar/layout choice) and the
 * GLOBAL_ADMIN permission gate (PermissionsContext.tsx). Previously each
 * consumer hand-listed the same view names independently; they drifted out
 * of sync when 'models' was added, leaving the permission gate un-updated
 * and letting non-admins reach Command Center's Models page unredirected.
 */
export function isCommandCenterView(activeView: ActiveView): activeView is CommandCenterView {
  return (
    activeView === 'commandCenter' ||
    activeView === 'defaultOrgConfig' ||
    activeView === 'models'
  );
}

const COMMAND_CENTER_SUBVIEW_BY_VIEW: Record<CommandCenterView, CommandCenterSubView> = {
  commandCenter: 'organizations',
  defaultOrgConfig: 'defaultOrgConfig',
  models: 'models',
};

/** Maps the current activeView to CommandCenterSidebar's highlighted row (or null outside Command Center / for views with no matching row). */
export function getCommandCenterSubView(activeView: ActiveView): CommandCenterSubView {
  return isCommandCenterView(activeView) ? COMMAND_CENTER_SUBVIEW_BY_VIEW[activeView] : null;
}

/** Nav destination within an org's detail view — matches design-frames/org-detail.html's nav-domains/nav-orgconfig/nav-allowlist. */
export type OrgDetailSection = 'domains' | 'orgconfig' | 'orgadmins' | 'allowlist';

const PATH_TO_VIEW: Record<string, Exclude<ActiveView, 'create' | null>> = {
  'chat': 'chat',
  'edit': 'edit',
  'add-files': 'addContent',
  'content': 'stats',
  'analytics': 'analytics',
  'security': 'manageUsers',
  'tools': 'tools',
  'history': 'history',
  'settings': 'settings',
  'publishing': 'publishing',
};

interface OrgUrlState {
  section: OrgDetailSection;
  siteId: string | null;
  /** Nav row to highlight — null for the bare org URL (fresh "Manage" entry, no row highlighted yet). */
  navHighlight: OrgDetailSection | null;
}

/**
 * Parses the `/command-center/org/:orgId/*` wildcard segment into the org
 * detail nav state it represents — the inverse of the paths built by
 * handleOrgDetailSection/handleToggleDomainsPanel/handleSelectDomain below.
 * Each nav destination gets its own distinct URL: bare (fresh entry,
 * unhighlighted), `domains`, `domains/:siteId`, `orgconfig`, `allowlist`.
 */
function parseOrgViewSegment(segment: string | null): OrgUrlState {
  if (!segment) return { section: 'domains', siteId: null, navHighlight: null };
  if (segment === 'orgconfig') return { section: 'orgconfig', siteId: null, navHighlight: 'orgconfig' };
  if (segment === 'orgadmins') return { section: 'orgadmins', siteId: null, navHighlight: 'orgadmins' };
  if (segment === 'allowlist') return { section: 'allowlist', siteId: null, navHighlight: 'allowlist' };
  if (segment === 'domains') return { section: 'domains', siteId: null, navHighlight: 'domains' };
  if (segment.startsWith('domains/')) {
    return { section: 'domains', siteId: segment.slice('domains/'.length), navHighlight: 'domains' };
  }
  return { section: 'domains', siteId: null, navHighlight: null };
}

interface SiteContextValue {
  sites: Site[];
  sitesLoading: boolean;
  sitesError: string | null;
  setSitesError: React.Dispatch<React.SetStateAction<string | null>>;
  selectedSite: Site | null;
  activeView: ActiveView;
  isCreating: boolean;
  createDomainType: DomainType;
  userOrgIds: string[];

  // Org detail view state (Command Center > Manage an org) — lifted here,
  // rather than kept local to CommandCenterPage, because the primary sidebar
  // (rendered by App.tsx, a sibling of the page content) needs to know when
  // an org is selected to swap in OrgDetailSidebar, per design-frames/
  // org-detail.html's nav living in the app's primary sidebar rail.
  selectedOrg: CCOrganization | null;
  orgDetailSection: OrgDetailSection;
  /** Which nav row is highlighted — distinct from orgDetailSection, since
   * design-frames/org-detail.html shows Overview content on first entry
   * with NO nav row highlighted until the user explicitly clicks one. */
  activeNavSection: OrgDetailSection | null;
  domainsPanelOpen: boolean;
  selectedDomainSiteId: string | null;
  handleSelectOrg: (org: CCOrganization) => void;
  handleBackFromOrgDetail: () => void;
  handleOrgDetailSection: (section: OrgDetailSection) => void;
  handleToggleDomainsPanel: () => void;
  handleSelectDomain: (siteId: string) => void;

  refreshSites: () => Promise<void>;
  handleSelectContext: (site: Site) => void;
  handleBack: () => void;
  handleOpenCreate: (domainType?: DomainType) => void;
  handleCloseCreate: () => void;
  handleDeleteSite: (site: Site, force?: boolean) => Promise<void>;
  handleSiteCreated: () => void;
  handleSiteUpdated: () => void;
  handleAddContent: () => void;
  handleStats: () => void;
  handleAnalytics: () => void;
  handleSettings: () => void;
  handlePublishing: () => void;
  handleTools: () => void;
  handleHistory: () => void;
  handleSources: () => void;
  handleSecurity: () => void;
  handleSampleContent: () => void;
  handleDocumentation: () => void;
  handleOrgModels: () => void;
  handleHomeView: () => void;
  handleCommandCenter: () => void;
  handleDefaultOrgConfig: () => void;
  handleModels: () => void;
}

const SiteContext = createContext<SiteContextValue | null>(null);

export function useSiteContext(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSiteContext must be used within a SiteProvider');
  return ctx;
}

interface SiteProviderProps {
  userOrgIds: string[];
  isAuthenticated: boolean;
  children: React.ReactNode;
}

export const SiteProvider: React.FC<SiteProviderProps> = ({ userOrgIds, isAuthenticated, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceMatch = useMatch('/domains/:workspaceId/*');
  const workspaceIdFromUrl = workspaceMatch?.params.workspaceId ?? null;
  const viewSegment = workspaceMatch?.params['*'] || null;
  const orgDetailMatch = useMatch('/command-center/org/:orgId/*');
  const orgIdFromUrl = orgDetailMatch?.params.orgId ?? null;
  const orgViewSegment = orgDetailMatch?.params['*'] || null;


  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createDomainType, setCreateDomainType] = useState<DomainType>('standard');
  const [selectedOrg, setSelectedOrg] = useState<CCOrganization | null>(null);
  const [orgDetailSection, setOrgDetailSection] = useState<OrgDetailSection>('domains');
  const [activeNavSection, setActiveNavSection] = useState<OrgDetailSection | null>(null);
  const [domainsPanelOpen, setDomainsPanelOpen] = useState(false);
  const [selectedDomainSiteId, setSelectedDomainSiteId] = useState<string | null>(null);

  const orgPathActiveView = useMemo((): ActiveView => {
    if (orgIdFromUrl) return 'commandCenter';
    switch (location.pathname) {
      case '/docs':
        return 'documentation';
      case '/org/security':
        return 'security';
      case '/org/sample-content':
        return 'sampleContent';
      case '/models':
        return 'orgModels';
      case '/command-center':
        return 'commandCenter';
      case '/command-center/default-org-config':
        return 'defaultOrgConfig';
      case '/command-center/models':
        return 'models';
      default:
        return null;
    }
  }, [location.pathname, orgIdFromUrl]);

  // Derive activeView from URL (org-level routes take precedence over create / domain views)
  let activeView: ActiveView = orgPathActiveView;
  if (!activeView) {
    if (isCreating) activeView = 'create';
    else if (viewSegment) activeView = PATH_TO_VIEW[viewSegment] ?? null;
  }

  // ---- Fetch sites ----
  useEffect(() => {
    if (!isAuthenticated || userOrgIds.length === 0) return;

    let cancelled = false;
    setSitesLoading(true);
    setSitesError(null);

    fetchSitesForOrgs(userOrgIds)
      .then((allSites) => { if (!cancelled) setSites(allSites); })
      .catch((error) => { if (!cancelled) setSitesError(getErrorMessage(error, i18n.t('errors.domains.loadFailed'))); })
      .finally(() => { if (!cancelled) setSitesLoading(false); });

    return () => { cancelled = true; };
  }, [isAuthenticated, userOrgIds]);

  // ---- URL -> State sync ----
  useEffect(() => {
    if (!workspaceIdFromUrl) {
      if (selectedSite) {
        setSelectedSite(null);
        setIsCreating(false);
      }
      return;
    }

    if (sites.length === 0 || sitesLoading) return;
    if (selectedSite?.site_id === workspaceIdFromUrl) return;

    const site = sites.find((s) => s.site_id === workspaceIdFromUrl);
    if (site) {
      setSelectedSite(site);
      setIsCreating(false);
    } else {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [workspaceIdFromUrl, sites, sitesLoading, selectedSite, navigate]);

  // ---- Org URL -> State sync ---- (mirrors the workspaceIdFromUrl sync
  // above.) Fetches the org summary only when genuinely arriving fresh — via
  // a direct load/refresh, a deep link, or switching orgs — where there's no
  // in-memory CCOrganization yet. When selectedOrg already matches
  // orgIdFromUrl, it skips the fetch but still re-derives section/
  // nav-highlight/selected-domain from the URL's wildcard segment, so
  // browser back/forward between domains/orgconfig/allowlist within the same
  // org stays in sync. This re-derivation is safe against the handlers below
  // (which set state AND call navigate() together) because it recomputes the
  // same values from the URL those handlers just navigated to — React bails
  // out on the redundant setState calls instead of fighting them.
  useEffect(() => {
    if (!orgIdFromUrl) {
      if (selectedOrg) {
        setSelectedOrg(null);
        setOrgDetailSection('domains');
        setActiveNavSection(null);
        setDomainsPanelOpen(false);
        setSelectedDomainSiteId(null);
      }
      return;
    }

    const { section, siteId, navHighlight } = parseOrgViewSegment(orgViewSegment);

    if (selectedOrg?.org_id === orgIdFromUrl) {
      // Org already loaded — resync nav/section state from the URL (e.g.
      // browser back/forward between domains/orgconfig/allowlist within the
      // same org) without re-fetching the org summary.
      setOrgDetailSection(section);
      setActiveNavSection(navHighlight);
      setDomainsPanelOpen(navHighlight === 'domains');
      setSelectedDomainSiteId(siteId);
      return;
    }

    let cancelled = false;
    fetchCCOrganizationSummary(orgIdFromUrl)
      .then((org) => {
        if (cancelled) return;
        setSelectedOrg(org);
        setOrgDetailSection(section);
        setActiveNavSection(navHighlight);
        setDomainsPanelOpen(navHighlight === 'domains');
        setSelectedDomainSiteId(siteId);
      })
      .catch(() => {
        if (!cancelled) navigate('/command-center', { replace: true });
      });
    return () => { cancelled = true; };
  }, [orgIdFromUrl, orgViewSegment, selectedOrg, navigate]);

  // ---- Refresh ----
  const refreshSites = useCallback(async () => {
    if (userOrgIds.length === 0) return;
    setSitesLoading(true);
    setSitesError(null);
    try {
      const allSites = await fetchSitesForOrgs(userOrgIds);
      setSites(allSites);
    } catch (error) {
      setSitesError(getErrorMessage(error, i18n.t('errors.domains.refreshFailed')));
    } finally {
      setSitesLoading(false);
    }
  }, [userOrgIds]);

  const handleSiteCreated = useCallback(() => { refreshSites(); }, [refreshSites]);
  const handleSiteUpdated = useCallback(() => { refreshSites(); }, [refreshSites]);

  // ---- Delete polling ----
  const handleSiteDeleted = useCallback((siteId: string) => {
    refreshSites();
    setSelectedSite((prev) => {
      if (prev?.site_id === siteId) {
        navigate(ROUTES.HOME, { replace: true });
        return null;
      }
      return prev;
    });
  }, [refreshSites, navigate]);

  const { startPoller: startDeletePoller } = useDeletePolling({ onDeleted: handleSiteDeleted });

  useEffect(() => {
    for (const site of sites) {
      if (site.status === 'deleting') {
        startDeletePoller(site.site_id, site.org_id);
      }
    }
  }, [sites, startDeletePoller]);

  const handleDeleteSite = useCallback(async (site: Site, force = false) => {
    try {
      await deleteSite(site.site_id, site.org_id, force);
      setSites((prev) =>
        prev.map((s) =>
          s.site_id === site.site_id ? { ...s, status: 'deleting' as const } : s
        )
      );
      setSelectedSite((prev) =>
        prev?.site_id === site.site_id ? { ...prev, status: 'deleting' as const } : prev
      );
      navigate(`/workspaces/${site.site_id}`, { replace: true });
      startDeletePoller(site.site_id, site.org_id);
    } catch (err) {
      // Re-throw the orchestrator-membership conflict so the caller can prompt
      // for a force-retry; all other failures stay contained here as before.
      if (err instanceof SiteDeletionConflictError) {
        throw err;
      }
      console.error('Failed to delete domain:', err);
      toast.negative(
        err instanceof SiteDeleteError && err.status === 409
          ? "Domain can't be deleted, a job is in progress for this domain."
          : "Domain can't be deleted.",
      );
    }
  }, [startDeletePoller, navigate]);

  // ---- Navigation callbacks ----
  const handleSelectContext = useCallback((site: Site) => {
    setSelectedSite(site);
    setIsCreating(false);
    navigate(`/domains/${site.site_id}`);
  }, [navigate]);

  const handleBack = useCallback(() => {
    setSelectedSite(null);
    setIsCreating(false);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleOpenCreate = useCallback((domainType: DomainType = 'standard') => {
    setSelectedSite(null);
    setCreateDomainType(domainType);
    setIsCreating(true);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleCloseCreate = useCallback(() => {
    setIsCreating(false);
  }, []);

  const handleAddContent = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/add-files`);
  }, [selectedSite, navigate]);

  const handleStats = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/content`);
  }, [selectedSite, navigate]);

  const handleAnalytics = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/analytics`);
  }, [selectedSite, navigate]);

  const handleSettings = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/settings?settingsTab=${DEFAULT_SETTINGS_TAB}`);
  }, [selectedSite, navigate]);

  const handlePublishing = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/publishing`);
  }, [selectedSite, navigate]);

  const handleTools = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/tools?toolsTab=entityDedup`);
  }, [selectedSite, navigate]);

  const handleHistory = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/history`);
  }, [selectedSite, navigate]);

  const handleSources = useCallback(() => {
    if (!selectedSite) return;
    navigate(`/domains/${selectedSite.site_id}/settings?settingsTab=sources`);
  }, [selectedSite, navigate]);

  const handleSecurity = useCallback(() => {
    setIsCreating(false);
    navigate(ROUTES.ORG_SECURITY);
  }, [navigate]);

  const handleSampleContent = useCallback(() => {
    setIsCreating(false);
    navigate(ROUTES.ORG_SAMPLE_CONTENT);
  }, [navigate]);

  const handleDocumentation = useCallback(() => {
    setIsCreating(false);
    navigate(ROUTES.DOCS);
  }, [navigate]);

  const handleOrgModels = useCallback(() => {
    setIsCreating(false);
    navigate(ROUTES.ORG_MODELS);
  }, [navigate]);

  const handleHomeView = useCallback(() => {
    setIsCreating(false);
    setSelectedSite(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleCommandCenter = useCallback(() => {
    setSelectedSite(null);
    setIsCreating(false);
    // A fresh Command Center entry always lands on the org list, never a
    // previously-viewed org's detail.
    setSelectedOrg(null);
    setOrgDetailSection('domains');
    setActiveNavSection(null);
    setDomainsPanelOpen(false);
    setSelectedDomainSiteId(null);
    navigate('/command-center');
  }, [navigate]);

  const handleDefaultOrgConfig = useCallback(() => {
    setSelectedSite(null);
    setIsCreating(false);
    navigate('/command-center/default-org-config');
  }, [navigate]);

  const handleModels = useCallback(() => {
    setSelectedSite(null);
    setIsCreating(false);
    navigate('/command-center/models');
  }, [navigate]);

  const handleSelectOrg = useCallback((org: CCOrganization) => {
    setSelectedOrg(org);
    setOrgDetailSection('domains');
    setActiveNavSection(null);
    setDomainsPanelOpen(false);
    setSelectedDomainSiteId(null);
    navigate(`/command-center/org/${org.org_id}`);
  }, [navigate]);

  const handleBackFromOrgDetail = useCallback(() => {
    setSelectedOrg(null);
    setOrgDetailSection('domains');
    setActiveNavSection(null);
    setDomainsPanelOpen(false);
    setSelectedDomainSiteId(null);
    navigate('/command-center');
  }, [navigate]);

  // Mirrors design-frames/org-detail.html's showView(): switching to a
  // non-domains section always closes the domains list panel and clears any
  // selected domain, matching the mockup's hideAllContent()+panel-display-none.
  // Each section gets its own URL, per design-frames/org-detail.html's
  // NAV_IDS — the same nav rows shown for each org id also being reachable
  // by URL, not just by clicking.
  const handleOrgDetailSection = useCallback((section: OrgDetailSection) => {
    setOrgDetailSection(section);
    setActiveNavSection(section);
    setDomainsPanelOpen(false);
    setSelectedDomainSiteId(null);
    if (selectedOrg) navigate(`/command-center/org/${selectedOrg.org_id}/${section}`);
  }, [selectedOrg, navigate]);

  // Mirrors showDomains(): clicking "Domains" always resets main content to
  // Overview (clearing any selected domain) and toggles the side panel. The
  // URL scheme already distinguishes the two states on the READ side
  // (parseOrgViewSegment: bare org URL -> navHighlight null/panel closed,
  // `.../domains` -> navHighlight 'domains'/panel open — the same bare URL
  // handleSelectOrg itself navigates to), so the WRITE side must target
  // whichever of those two URLs matches the direction just toggled to,
  // instead of always targeting `.../domains` — otherwise closing the panel
  // never has a URL to go back to and the address bar silently stops
  // tracking the toggle.
  const handleToggleDomainsPanel = useCallback(() => {
    const next = !domainsPanelOpen;
    setOrgDetailSection('domains');
    setActiveNavSection('domains');
    setSelectedDomainSiteId(null);
    setDomainsPanelOpen(next);
    if (selectedOrg) {
      const target = next
        ? `/command-center/org/${selectedOrg.org_id}/domains`
        : `/command-center/org/${selectedOrg.org_id}`;
      if (location.pathname !== target) navigate(target);
    }
  }, [selectedOrg, navigate, location.pathname, domainsPanelOpen]);

  const handleSelectDomain = useCallback((siteId: string) => {
    setOrgDetailSection('domains');
    setActiveNavSection('domains');
    setSelectedDomainSiteId(siteId);
    if (selectedOrg) navigate(`/command-center/org/${selectedOrg.org_id}/domains/${siteId}`);
  }, [selectedOrg, navigate]);

  const value = useMemo<SiteContextValue>(() => ({
    sites,
    sitesLoading,
    sitesError,
    setSitesError,
    selectedSite,
    activeView,
    isCreating,
    createDomainType,
    userOrgIds,
    selectedOrg,
    orgDetailSection,
    activeNavSection,
    domainsPanelOpen,
    selectedDomainSiteId,
    handleSelectOrg,
    handleBackFromOrgDetail,
    handleOrgDetailSection,
    handleToggleDomainsPanel,
    handleSelectDomain,
    refreshSites,
    handleSelectContext,
    handleBack,
    handleOpenCreate,
    handleCloseCreate,
    handleDeleteSite,
    handleSiteCreated,
    handleSiteUpdated,
    handleAddContent,
    handleStats,
    handleAnalytics,
    handleSettings,
    handlePublishing,
    handleTools,
    handleHistory,
    handleSources,
    handleSecurity,
    handleSampleContent,
    handleDocumentation,
    handleOrgModels,
    handleHomeView,
    handleCommandCenter,
    handleDefaultOrgConfig,
    handleModels,
  }), [
    sites, sitesLoading, sitesError, selectedSite, activeView, isCreating, createDomainType, userOrgIds,
    selectedOrg, orgDetailSection, activeNavSection, domainsPanelOpen, selectedDomainSiteId,
    handleSelectOrg, handleBackFromOrgDetail, handleOrgDetailSection, handleToggleDomainsPanel, handleSelectDomain,
    refreshSites, handleSelectContext, handleBack, handleOpenCreate, handleCloseCreate,
    handleDeleteSite, handleSiteCreated, handleSiteUpdated, handleAddContent, handleStats,
    handleAnalytics, handleSettings, handlePublishing, handleTools, handleHistory, handleSources,
    handleSecurity, handleSampleContent, handleDocumentation, handleOrgModels, handleHomeView,
    handleCommandCenter, handleDefaultOrgConfig,
    handleModels,
  ]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};
